import { maxUint256 } from 'viem';
import { z } from 'zod';

import { reportBrokenField } from '@/lib/tolerant-rows';

const UINT256_DIGITS = /^\d{1,78}$/;
const MAX_UINT256_DIGITS = maxUint256.toString();

/**
 * Whether a served string is a uint256 in base units: decimal digits only,
 * at most 78 of them, and at most 2^256 - 1. Checked on the string itself,
 * so an oversized value is refused before any `BigInt` is built from it:
 * digit strings of the same length order the same way as their values.
 */
function isBaseUnits(value: string): boolean {
  return (
    UINT256_DIGITS.test(value) &&
    (value.length < MAX_UINT256_DIGITS.length || value <= MAX_UINT256_DIGITS)
  );
}

/**
 * A served uint256 in base units, kept as its decimal string. The one
 * boundary check every base units field shares: a value out of range is
 * malformed, and follows its field's own tolerant path.
 */
export const baseUnitsString = z
  .string()
  .refine(isBaseUnits, 'Expected base units');

/**
 * The vault model keeps exact on chain amounts as base unit decimal strings,
 * not `bigint`: the server data cache serialises with JSON, and
 * `JSON.stringify` throws on a `bigint`. Read one back as an exact integer
 * here, at the point of use. Null for an unknown or malformed amount (one
 * past uint256 included), never 0: an unread bound and a bound of nothing
 * are different facts.
 */
export function baseUnits(value: string | null | undefined): bigint | null {
  return value != null && isBaseUnits(value) ? BigInt(value) : null;
}

/**
 * `part` as a share of `whole`, both base units of the same token from the
 * same read, in basis points: floored (never above what was measured) and
 * at most 100%. Null when either does not read or the whole is zero: a
 * share of nothing is not a share.
 */
function shareOfBaseUnits(
  part: string | null | undefined,
  whole: string | null | undefined
): number | null {
  const top = baseUnits(part);
  const bottom = baseUnits(whole);
  if (top == null || bottom == null || bottom === 0n) return null;
  const bps = (top * 10_000n) / bottom;
  return bps > 10_000n ? 10_000 : Number(bps);
}

/**
 * The share of assets a vault can pay out now (API 1.155.0), derived from
 * the amount and the total it came with, never the served ratio beside
 * them: a stale ratio would read "40 USDC (99% of assets)". The served one
 * is a cross-check only, and a disagreement past 1 bp is reported once by
 * field, never by value.
 */
/**
 * The amount a vault can pay out now, as measured: an amount served beside a
 * reason it was not measured is a contradiction, so it is reported once and
 * read as not measured. Never shown as a live figure.
 */
export function measuredRedeemableNow(
  redeemableNow: string | null | undefined,
  reason: string | null | undefined,
  label: string
): string | null {
  if (redeemableNow == null) return null;
  if (reason == null) return redeemableNow;
  reportBrokenField(label, 'redeemableNowReason');
  return null;
}

export function redeemableShareBps(
  redeemableNow: string | null | undefined,
  totalAssets: string | null | undefined,
  servedBps: number | null | undefined,
  label: string
): number | null {
  const derived = shareOfBaseUnits(redeemableNow, totalAssets);
  if (
    derived != null &&
    servedBps != null &&
    Math.abs(servedBps - derived) > 1
  ) {
    reportBrokenField(label, 'redeemableNowBps');
  }
  return derived;
}
